"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ContactForm from "./ContactForm";

const services = [
  {
    title: "Architectural Design",
    description:
      "End-to-end architectural solutions from concept development to execution, grounded in context and material clarity.",
  },
  {
    title: "Urban Planning",
    description:
      "Strategic planning and large-scale design approaches focused on livability, density, and long-term sustainability.",
  },
  {
    title: "Interior Design",
    description:
      "Interior environments that balance function, material expression, and spatial experience.",
  },
  {
    title: "Sustainable Design",
    description:
      "Climate-responsive design strategies integrating passive systems, material efficiency, and long-term performance.",
  },
  {
    title: "Project Management",
    description:
      "Comprehensive coordination across consultants, timelines, and site execution to ensure design intent is realized.",
  },
  {
    title: "Design Consultancy",
    description:
      "Advisory services for design direction, feasibility studies, and architectural review at various project stages.",
  },
];

const ServicesSection = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <section className="relative py-32 bg-white text-black">
      <div className="max-w-[95%] mx-auto">
        {/* SECTION TITLE */}
        <motion.h2
          className="text-4xl md:text-5xl font-semibold tracking-wide mb-24"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          SERVICES
        </motion.h2>

        {/* MAIN LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-x-32 items-start">
          {/* LEFT: SERVICES LIST */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-24 gap-y-24">
            {services.map((service, index) => {
              const isActive = activeIndex === index;

              return (
                <motion.div
                  key={service.title}
                  className="border-b border-black pb-10 cursor-pointer min-h-[130px]"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-start gap-6">
                    <span className="w-6 text-xl font-light leading-none">
                      {isActive ? "−" : "+"}
                    </span>

                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-medium tracking-wide">
                        {service.title}
                      </h3>

                      <AnimatePresence>
                        {isActive && (
                          <motion.p
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="mt-4 text-sm leading-relaxed text-neutral-600"
                          >
                            {service.description}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* RIGHT: CONTACT FORM */}
          <motion.div
            className="sticky top-32"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <ContactForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
